import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Actions from '../../actions/actions';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import withDataLoaded from '../with_data_loaded';
import { authorizedSignerListLoader, userLoader } from '../../actions/data_loaders';
import PageSpinner from '../shared/page_spinner';
import { UserAdminDataGrid, UserAdminNoRows, UserAdminToolbar } from './components/user_admin_data_grid';
import SearchHelper from '../../helpers/search_helper';
import Box from '@mui/material/Box';
import { IconButton } from '@mui/material';
import FeaturePermissionsHelper from '../../helpers/feature_permissions_helper';

const deleteSignerModal = (id, coid) =>
  Actions.requestModals({
    title: 'Are you sure you want to delete this authorized signer from the system?',
    modalClass: 'user-admin-item-list-modal',
    cancelButtonText: 'Cancel',
    okButtonText: 'Yes, Remove',
    modalID: 'user-admin-signer-delete-modal',
    okID: 'user-admin-signer-delete-modal-confirm',
    cancelID: 'user-admin-signer-delete-modal-cancel',
    okClass: 'action-warning user-admin-delete-confirm',
    cancelClass: 'user-admin-delete-cancel',
    okProcess: async () => await Actions.deleteSigner({ id, coid }),
  });

function AuthorizedSignerList() {
  const currUser = useSelector((state) => state.user);
  const view = useSelector((state) => state.view);
  const authorizedSignerList = useSelector((state) => state.authorizedSignerList);

  const [signersToDisplay, setSignersToDisplay] = useState(authorizedSignerList);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (authorizedSignerList?.length) {
      setSignersToDisplay(SearchHelper.search(searchText, authorizedSignerList, ['fullName', 'email', 'branchName']));
    }
  }, [authorizedSignerList, searchText]);

  const customToolbar = useMemo(() => {
    return UserAdminToolbar(
      'Authorized Signers',
      true,
      <button
        id="qa-admin-list-add-authorized-signer-button"
        className="action-primary action-alt add-item-button"
        onClick={() => {
          Actions.clearAuthorizedSigner();
          Actions.setRoute(`/account/companies/${view.coid}/signers/new`);
        }}
        disabled={!FeaturePermissionsHelper.hasPermission(currUser, 'AUTHORIZED_SIGNER', 'CREATE')}
      >
        <AddIcon className="add-item-icon" />
        Add Authorized Signer
      </button>
    );
  }, [view?.coid, currUser]);

  if (currUser === null) {
    return <PageSpinner />;
  }

  const defaultCol = { flex: 1 };

  const columns = [
    { field: 'fullName', headerName: 'Name', flex: 3 },
    { field: 'email', headerName: 'Email', flex: 4 },
    { field: 'branchName', headerName: 'Branch', ...defaultCol },
    {
      headerName: 'Default',
      field: 'isDefault',
      ...defaultCol,
      renderCell: ({ value }) => (
        <Box style={{ display: 'flex', justifyContent: 'center' }}>
          {value ? <CheckIcon id="qa-default-signer" /> : ''}
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'Actions',
      sortable: false,
      renderCell: ({ value }) => (
        <div style={{ display: 'flex' }}>
          <span id={'qa-actions-deactivate'}>
            <IconButton
              aria-label="deactivate"
              title="remove signer"
              onClick={(event) => {
                event.stopPropagation();
                deleteSignerModal(value, view?.coid);
              }}
              disabled={!FeaturePermissionsHelper.hasPermission(currUser, 'AUTHORIZED_SIGNER', 'DELETE')}
            >
              <DeleteOutlinedIcon />
            </IconButton>
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="user-admin-page datagrid-page" id={'qa-authorized-signers-list-page'}>
      <UserAdminDataGrid
        columns={columns}
        rows={signersToDisplay}
        hideFooter
        onCellClick={({ row }) => {
          Actions.clearAuthorizedSigner();
          Actions.setRoute(`/account/companies/${view.coid}/signers/${row.id}`);
        }}
        components={{
          LoadingOverlay: PageSpinner,
          Toolbar: customToolbar,
          NoRowsOverlay: UserAdminNoRows,
        }}
        componentsProps={{
          toolbar: {
            value: searchText,
            clearSearch: () => setSearchText(''),
            onChange: (event) => setSearchText(event.target.value),
          },
        }}
        rowClassFunc={(row) => `qa-signer-email-${row.email.replaceAll(/@|\./g, '')}`}
      />
    </div>
  );
}

const connectedAuthorizedSignerList = withDataLoaded({
  WithData: AuthorizedSignerList,
  WithoutData: PageSpinner,
  data: [userLoader, authorizedSignerListLoader],
});

export default connectedAuthorizedSignerList;
